/**
 * A one-slot latch for the service worker's "a new worker is waiting" signal (D47).
 *
 * `virtual:pwa-register` fires `onNeedRefresh` as soon as it sees a waiting worker, but
 * the `Updater` that receives it is only built inside `onRegisteredSW`, which runs later.
 * On iOS the worker script is re-fetched during navigation, so the waiting worker can be
 * there before the registration callback runs and the signal lands with nothing to catch
 * it. The latch remembers a signal that arrives early and replays it once the receiver
 * exists, so the pill appears either way.
 */

export interface NeedRefreshLatch {
  /** `onNeedRefresh`, or anything else that has found a waiting worker. */
  signal(): void;
  /** Wire up the receiver. A signal buffered before this point is replayed once, now. */
  attach(receiver: () => void): void;
}

export function createNeedRefreshLatch(): NeedRefreshLatch {
  let receiver: (() => void) | null = null;
  let buffered = false;
  return {
    signal(): void {
      if (receiver) receiver();
      else buffered = true;
    },
    attach(next: () => void): void {
      receiver = next;
      if (!buffered) return;
      buffered = false;
      next();
    },
  };
}
